import React, { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useHistory, useRouteMatch } from "react-router";
import {
  Box,
  Button,
  Divider,
  Hidden,
  Link,
  TableCell,
  TableRow,
  Theme,
  Typography,
} from "@mui/material";
import {
  BorderColor as BorderColorOutlinedIcon,
  ShoppingCartOutlined as ShoppingCartOutlinedIcon,
} from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { startCase } from "lodash";
import TagManager from "react-gtm-module";

import { ReactComponent as PartnerFusionSvg } from "assets/images/icons/partners_fusion.svg";
import { QuoteConfirmDialog } from "components/QuoteConfirmDialog";
import { Menu, MenuItem } from "components/shared";
import {
  IdsExpandMore as ArrowDropDownIcon,
  IdsExpandLess as ArrowDropUpIcon,
} from "components/shared/svgIcons";
import { Para14Text } from "components/shared/Typography";
import { QUOTES } from "helpers/apis";
import useAxios from "helpers/axios";
import useCatalog from "hooks/useCatalog";
import useCatalogDetail from "hooks/useCatalogDetail";
import { colors } from "theme";
import { DesignType, IDesign, PlatformType, Workflow } from "types/design";
import { IQuote } from "types/quote";

const useStyles = makeStyles((theme: Theme) => ({
  "@global": {
    "@keyframes attentionRow": {
      "0%": {
        backgroundColor: "inherit",
        borderColor: colors.grey[200],
        outlineColor: colors.grey[200],
        outlineWidth: 1,
      },
      "50%": {
        backgroundColor: theme.palette.secondary.light,
        borderColor: theme.palette.secondary.main,
        outlineColor: theme.palette.secondary.main,
        outlineWidth: 2,
      },
      "100%": {
        backgroundColor: "inherit",
        borderColor: colors.grey[200],
        outlineColor: colors.grey[200],
        outlineWidth: 1,
      },
    },
  },
  basicRow: {
    backgroundColor: theme.palette.background.paper,
    "& > .MuiTableCell-root": {
      fontSize: theme.typography.pxToRem(14),
      lineHeight: 1.5,
      borderTop: `1px solid ${colors.grey[200]}`,
      borderBottom: "none",
      "&:first-child": {
        borderLeft: `1px solid ${colors.grey[200]}`,
        borderTopLeftRadius: 4,
      },
      "&:last-child": {
        borderRight: `1px solid ${colors.grey[200]}`,
        borderTopRightRadius: 4,
      },
      "&.active": {
        borderBottom: `1px solid ${theme.palette.primary.dark}`,
      },
      animation: ({ animation }: any) =>
        animation ? "attentionRow 2s ease 0s 5 normal forwards" : "none",
    },
    "& > .MuiTableCell-sizeSmall": {
      padding: "14px 13px 14px 16px",
    },
  },
  panelName: { color: theme.palette.primary.main },
  expandRow: {
    backgroundColor: theme.palette.background.paper,
    "& > .MuiTableCell-root": {
      borderBottom: `1px solid ${colors.grey[200]}`,
      paddingTop: 0,
      "&:first-child": {
        borderLeft: `1px solid ${colors.grey[200]}`,
        borderBottomLeftRadius: 4,
      },
      "&:last-child": {
        borderRight: `1px solid ${colors.grey[200]}`,
        borderBottomRightRadius: 4,
      },
      animation: ({ animation }: any) =>
        animation ? "attentionRow 2s ease 0s 5 normal forwards" : "none",
    },
  },
  detailRow: {
    "& > .MuiTableCell-root": {
      borderTop: "none",
      borderBottom: "none !important",
      paddingTop: 0,
      paddingBottom: 0,
      borderRadius: "0 !important",
    },
  },
  expandToggle: {
    "&:hover": {
      backgroundColor: colors.grey[500],
      cursor: "pointer",
      "& > a": {
        textDecoration: "underline",
      },
    },
    "& > a": {
      display: "flex",
    },
  },
  spacingRow: {
    height: 18,
    width: "100%",
  },
  divider: {
    backgroundColor: colors.grey[500],
    marginTop: 1,
  },
  platformSelection: {
    //width: 165,
  },
  menu: {
    width: 165,
  },
  menuLabel: {
    display: "flex",
    justifyContent: "space-between",
    whiteSpace: "nowrap",
  },
  quoteError: {
    textAlign: "right",
    fontWeight: 400,
    letterSpacing: 0.1,
  },
  platformBtn: {
    "& .MuiButton-iconSizeSmall > *:first-child": {
      fontSize: theme.typography.pxToRem(24),
    },
    "& .MuiButton-endIcon.MuiButton-iconSizeSmall": {
      marginLeft: theme.spacing(0.25),
      marginRight: theme.spacing(-1),
    },
  },
  buttonWithHiddenIcon: {
    '& span[class*="label"]': { whiteSpace: "nowrap" },
    [theme.breakpoints.only("xs")]: {
      "& .MuiButton-startIcon.MuiButton-iconSizeSmall": {
        marginRight: 0,
        marginLeft: 0,
      },
    },
    [theme.breakpoints.up("sm")]: {
      "& .MuiButton-startIcon.MuiButton-iconSizeSmall": {
        marginLeft: theme.spacing(-1),
      },
    },
  },
  mobileSize: { [theme.breakpoints.only("xs")]: { height: 32 } },
}));

const Row = ({
  row,
  canQuote = true,
}: {
  row: IDesign;
  canQuote?: boolean; // Suggested Panels can't make a quote
}) => {
  const history = useHistory();
  const { path } = useRouteMatch();
  const [detailCol, setDetailCol] = useState<keyof IDesign | null>(null);
  const [quoteError, setQuoteError] = useState<boolean>(false);
  const [platform, setPlatform] = useState<PlatformType>();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [customPanelAnimation, setCustomPanelAnimation] =
    useState<boolean>(false);
  const { customPanels, rowsData, setCustomPanels, setRowsData } = useCatalog();
  const { setCatalog, workflow } = useCatalogDetail();
  const { fetchData, loading } = useAxios({
    config: { url: QUOTES, method: "POST" },
    callOnInit: false,
  });
  const isCatalogPanel: boolean = row.designType === DesignType.CATALOG_PANEL;
  const isQuoteRequested: boolean = Boolean(row.requisitionId);
  const isCustomPanel: boolean = "panelspresent" in row; // If a panelspresent exists, it's a custom panel
  const isSuggestedPanel: boolean =
    row.designType === DesignType.SUGGESTED_PANEL;

  const isKFPVisible = useMemo(
    () =>
      detailCol === "coveredGenes" &&
      row.coveredPartnerTargets &&
      workflow?.name === Workflow.FUSION,
    [detailCol, workflow?.name, row.coveredPartnerTargets]
  );

  useEffect(() => {
    if (!!platform) {
      setQuoteError(false);
    }
  }, [platform]);

  // Show an animation for newly added custom panel
  useEffect(() => {
    const oldCountOfCustomPanels = Number(
      localStorage.getItem("cntOfCustomPanels")
    );

    if (
      customPanels.length > 0 &&
      oldCountOfCustomPanels < customPanels.length
    ) {
      setCustomPanelAnimation(true);
    } else {
      setCustomPanelAnimation(false);
    }
  }, [customPanels]);

  const toggleDetailSection = (col: keyof IDesign) => {
    setDetailCol((detailCol) => (detailCol === col ? null : col));
  };

  const PlatformSelection = ({ disabled }: { disabled: boolean }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const classes = useStyles({ platformDropdownActive: Boolean(anchorEl) });

    const openMenu = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    return (
      <>
        <Button
          color="primary"
          variant="outlined"
          size="small"
          endIcon={
            Boolean(anchorEl) ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />
          }
          onClick={openMenu}
          classes={{
            // label: classes.menuLabel,
            root: classes.platformBtn,
          }}
          disabled={disabled}
          fullWidth
        >
          {platform ? startCase(platform) : "Select platform"}
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          onClick={() => setAnchorEl(null)}
          classes={{ paper: classes.menu }}
        >
          {(Object.keys(PlatformType) as Array<keyof typeof PlatformType>).map(
            (platformType: keyof typeof PlatformType) => (
              <MenuItem
                key={PlatformType[platformType]}
                size="sm"
                disabled={
                  PlatformType[platformType] === PlatformType.IONTORRENT &&
                  workflow?.name !== Workflow.FUSION
                }
                onClick={() => setPlatform(PlatformType[platformType])}
              >
                {startCase(PlatformType[platformType])}
              </MenuItem>
            )
          )}
        </Menu>
      </>
    );
  };

  const onQuoteRequest = () => {
    if (!platform) {
      setQuoteError(true);
    } else {
      TagManager.dataLayer({
        dataLayer: {
          event: "request_quote",
          pagePath: path,
          pageTitle: "Evaluate Panel Options",
        },
      });

      fetchData(
        {
          data: {
            platformType: platform,
            [isCatalogPanel ? "design" : "customPanel"]: row.id,
          },
        },
        ({ response }: any) => {
          if (response?.data) {
            setRequisitionId(response.data as IQuote);
            setOpenDialog(true);
          }
        }
      );
    }
  };

  const setRequisitionId = useCallback((quote: IQuote) => {
    if (quote.design) {
      setRowsData((rows: Partial<IDesign>[]) =>
        rows.map((panel: Partial<IDesign>) => {
          if (panel.id === quote.design) {
            return { ...panel, requisitionId: quote.requisitionId };
          }
          return panel;
        })
      );
    }
    if (quote.customPanel) {
      setCustomPanels((rows: Partial<IDesign>[]) =>
        rows.map((panel: Partial<IDesign>) => {
          if (panel.id === quote.customPanel) {
            return { ...panel, requisitionId: quote.requisitionId };
          }
          return panel;
        })
      );
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  const classes = useStyles({
    animation: customPanelAnimation && rowsData[0].id === row.id,
  });

  return (
    <>
      <TableRow className={classes.basicRow}>
        <TableCell>
          <Typography variant="h6" component="span">
            {row.name}
          </Typography>
        </TableCell>
        <TableCell>
          {isCatalogPanel
            ? "Catalog Panel"
            : row.designType === DesignType.SUGGESTED_PANEL
            ? "Suggested Panel"
            : "Custom Panel"}
        </TableCell>
        <TableCell>{row.readsRecommended}</TableCell>
        <TableCell
          className={`${detailCol === "coveredGenes" ? "active" : ""} ${
            classes.expandToggle
          }`}
          onClick={() => toggleDetailSection("coveredGenes")}
        >
          <Link
            color={detailCol === "coveredGenes" ? "primary" : "secondary"}
            variant="h6"
          >
            {row.numberCoveredGenes}
            {row.coveredPartnerGenes?.length > 0 && (
              <Box marginLeft={1} display="flex">
                <PartnerFusionSvg width={20} height={20} />
              </Box>
            )}
          </Link>
        </TableCell>
        <TableCell
          className={`${detailCol === "extraGenes" ? "active" : ""} ${
            classes.expandToggle
          }`}
          onClick={() => toggleDetailSection("extraGenes")}
        >
          <Link
            color={detailCol === "extraGenes" ? "primary" : "secondary"}
            variant="h6"
          >
            {row.numberExtraGenes}
          </Link>
        </TableCell>
        <TableCell
          className={`${detailCol === "uncoveredGenes" ? "active" : ""} ${
            classes.expandToggle
          }`}
          onClick={() => toggleDetailSection("uncoveredGenes")}
        >
          <Link
            color={detailCol === "uncoveredGenes" ? "primary" : "secondary"}
            variant="h6"
          >
            {row.numberUncoveredGenes}
          </Link>
        </TableCell>
      </TableRow>
      <TableRow
        className={`${classes.expandRow} ${classes.detailRow} ${
          detailCol ? "" : "hidden"
        }`}
      >
        <TableCell colSpan={6}>
          {detailCol && (
            <>
              <Divider className={classes.divider} />
              <Box my={3}>
                <Typography variant="h5" component="p">
                  {detailCol === "coveredGenes"
                    ? `${
                        workflow?.name === Workflow.FUSION
                          ? "Exact Matching Targets"
                          : "Matching Genes"
                      } (${row.numberCoveredTargetGenes})`
                    : startCase(detailCol)}
                </Typography>
                {row[detailCol] && (
                  <Box mt={2}>
                    <Typography variant="body2">
                      {(detailCol === "coveredGenes"
                        ? row.coveredTargetGenes
                        : (row[detailCol] as string[])
                      ).join(", ")}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </TableCell>
      </TableRow>
      <TableRow
        className={`${classes.expandRow} ${classes.detailRow}  ${
          isKFPVisible ? "" : "hidden"
        }`}
      >
        <TableCell colSpan={6}>
          {isKFPVisible && (
            <>
              <Divider className={classes.divider} />
              <Box my={3}>
                <Box display="flex" alignItems="center" gap="4px">
                  <PartnerFusionSvg width={20} height={20} />
                  <Typography variant="h5" component="p">
                    Targets with Known Fusion Partners (
                    {row.numberCoveredPartnerTargets})
                  </Typography>
                </Box>
                {Object.keys(row.coveredPartnerTargets)
                  .sort()
                  .map((key: string) => (
                    <Box mt={2} display="flex" flexDirection="column" gap="16px">
                      <Typography variant="h6" component="p">
                        Your Query:{" "}
                        {row.coveredGenes.find(
                          (gene) => gene.toLowerCase() === key.toLowerCase()
                        ) || ""}
                      </Typography>
                      <Para14Text>
                        Known Partners:{" "}
                        {(row.coveredPartnerTargets[key] as string[]).join(
                          ", "
                        )}
                      </Para14Text>
                    </Box>
                  ))}
              </Box>
            </>
          )}
        </TableCell>
      </TableRow>
      <TableRow className={classes.expandRow}>
        <TableCell colSpan={6}>
          <Divider className={classes.divider} />
          <Box
            mt={1}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Button
              variant="text"
              size="small"
              color="secondary"
              onClick={() => {
                if (isSuggestedPanel) {
                  setCatalog(row);
                }
                history.push(
                  `${path}/${
                    isSuggestedPanel
                      ? "suggested"
                      : isCustomPanel
                      ? "custom"
                      : "catalog"
                  }-panel-detail/${isSuggestedPanel ? "" : row.id}`
                );
              }}
            >
              View Detail
            </Button>
            <Box ml={1}>
              <Button
                startIcon={
                  <Hidden smUp>
                    <BorderColorOutlinedIcon />
                  </Hidden>
                }
                variant="outlined"
                size="small"
                color="primary"
                className={classes.buttonWithHiddenIcon}
                onClick={() => {
                  if (isSuggestedPanel) {
                    setCatalog(row);
                  }
                  history.push(
                    `${path}/${
                      isSuggestedPanel
                        ? "suggested"
                        : isCustomPanel
                        ? "custom"
                        : "catalog"
                    }-panel-customize/${isSuggestedPanel ? "" : row.id}`
                  );
                }}
                disabled={!isCatalogPanel && isQuoteRequested}
              >
                <Hidden only="xs">Customize panel</Hidden>
              </Button>
            </Box>
            {canQuote && (
              <Box mx={1} className={classes.platformSelection}>
                <PlatformSelection
                  disabled={!isCatalogPanel && isQuoteRequested}
                />
              </Box>
            )}
            {canQuote && (
              <Button
                startIcon={
                  <Hidden smUp>
                    <ShoppingCartOutlinedIcon />
                  </Hidden>
                }
                color="primary"
                variant="contained"
                size="small"
                className={clsx(
                  classes.buttonWithHiddenIcon,
                  classes.mobileSize
                )}
                onClick={onQuoteRequest}
                disabled={loading || (!isCatalogPanel && isQuoteRequested)}
              >
                <Hidden only="xs">
                  {!isCatalogPanel && isQuoteRequested
                    ? "Quote requested"
                    : "Request quote"}
                </Hidden>
              </Button>
            )}
          </Box>
          {quoteError && (
            <Box mt={1} width={1} display="flex" justifyContent="flex-end">
              <Typography
                variant="subtitle2"
                color="error"
                className={classes.quoteError}
              >
                You must select a platform before requesting a quote
              </Typography>
            </Box>
          )}
        </TableCell>
      </TableRow>
      <TableRow className={classes.spacingRow} role="presentation" />

      <QuoteConfirmDialog
        open={openDialog && !!row.requisitionId}
        onClose={() => setOpenDialog(false)}
        requisitionId={row.requisitionId}
      />
    </>
  );
};

export default Row;
